/**
 * Chrome-style tab strip for switching between concurrently live runs.
 *
 * User-approved as a genuinely new component (not a `packages/ui` primitive
 * recomposition) — see the coordinator's follow-up on this PR. It stays in
 * this feature folder rather than the shared kit: there is exactly one
 * caller (`LiveRunsSection`), and its rendering — a run-target *kind* icon
 * (suite/schedule/tag/ad hoc) per collapsed tab — is specific to run targets,
 * not a general-purpose tab shape. Promoting it before a second caller wants
 * the same thing would be exactly the speculative surface this repo's
 * simplicity rules forbid.
 *
 * Behavior, modeled on the ARIA APG "tabs, manual activation" pattern, and
 * real Chrome tab behavior for layout:
 * - Tabs never wrap (`flex-nowrap`) — they share the row's width. The
 *   selected tab is `flex-1 min-w-0`; collapsed tabs are `flex-none`, sized
 *   to their icon+dot content and staying that way regardless of hover or
 *   focus — only the *selection* changes a tab's width.
 * - Selecting a tab animates its label open via a `grid-template-columns`
 *   0fr→1fr transition, while the previously-selected tab's label animates
 *   closed the same way — both driven off the one `isSelected` boolean, so
 *   there's no separate hover/focus state to fight it. The button's own
 *   `flex-grow`/`flex-basis` transition on the same trigger and duration, so
 *   the outer width and the inner label reveal move together instead of one
 *   snapping ahead of the other. `motion-reduce:` disables both.
 * - Collapsed tabs get real chrome (a filled, bordered background), not just
 *   a bare icon — that's what reads as "these are tabs" rather than two
 *   floating glyphs.
 * - Every tab drops its bottom border and the row sits with zero gap above
 *   `LiveRunCard`, whose top border/rounding is removed in turn (`attached`
 *   prop) — the same lockup technique this page already uses to fuse the
 *   filter bar / sparkline / table (see `filter-lockup-table` and
 *   `FilterToolbar`'s `attached`). The strip and the card read as one object.
 * - Hovering never selects; only click or Enter/Space (native `<button>`
 *   behavior) does. Arrow/Home/End keys move a roving tabindex across tabs
 *   without changing the selection, exactly like a real tablist — the
 *   `packages/ui` `UnderlineTabs` this replaces has no arrow-key handling at
 *   all, so this hand-rolls that piece rather than copying its gap.
 *
 * A collapsed tab's label has no hover reveal of its own — instead each tab
 * is wrapped in a `TooltipTrigger`/`Tooltip` carrying the full label (with
 * the disambiguating `· Xd ago` suffix when it applies). The full label text
 * is still always in the DOM (clipped visually, never string-truncated) and
 * set as `aria-label`, which is what actually carries the accessible name
 * for a collapsed icon-only tab — the tooltip is a sighted-hover affordance
 * on top of that, not a replacement for it, so there's no native `title`
 * here (that would just double up with the tooltip). Each tab is wired to
 * the single visible panel via `aria-controls`/`id` (see `LiveRunsSection`,
 * which renders the matching `role="tabpanel"`).
 *
 * @module ui/pages/runs/test-runs/LiveRunTabs
 */
import * as React from 'react';
import { Clock, Lightning, Stack, Tag, CircleNotch, type Icon } from '@phosphor-icons/react';
import { cn, Tooltip, TooltipTrigger } from '@chatsdet/ui';
import type { RunTargetKind } from '@chatsdet/types';
import type { LiveRun } from '../../../../features/workflows/useLiveWorkflowTestExecutions';
import { formatRelativeStartTime } from './relativeStartTime';

// Reuses whatever this concept already renders as elsewhere rather than
// inventing a new icon vocabulary: `Stack` is the sidebar's "Suites" icon
// (`AppShell.tsx`), `Clock` is the schedule icon already used for the
// "Upcoming" rail rows and the schedule modal, and `Lightning` is the
// existing "ad hoc" icon from the planning cards (`PlanOutcomeCard.tsx`).
// Only `Tag` has no prior art in this codebase — it's the literal Phosphor
// icon for the concept.
const KIND_ICON: Record<RunTargetKind, Icon> = {
  suite: Stack,
  schedule: Clock,
  tag: Tag,
  adhoc: Lightning,
};

export function liveRunTabId(suiteId: string): string {
  return `live-run-tab-${suiteId}`;
}

export type LiveRunTabsProps = {
  runs: LiveRun[];
  selectedRunId: string;
  onRunSelect: (id: string) => void;
  /** id of the single `role="tabpanel"` every tab controls (see `LiveRunsSection`). */
  panelId: string;
};

export function LiveRunTabs({ runs, selectedRunId, onRunSelect, panelId }: LiveRunTabsProps) {
  const [focusedId, setFocusedId] = React.useState(selectedRunId);
  const tabRefs = React.useRef(new Map<string, HTMLButtonElement>());

  // `focusedId` is only ever set from real focus/keyboard events below, but
  // the run it points at can disappear out from under it — a completed run
  // is dropped from `runs` 30s after finishing (see the cleanup effect in
  // `useLiveWorkflowTestExecutions`). If that happened to be the focused run,
  // no tab would carry `tabIndex={0}` and the whole tablist would become
  // unreachable by keyboard. Deriving the effective id per render (rather
  // than an effect that reconciles state) means a stale id can never persist
  // past the next render.
  const effectiveFocusedId = runs.some((r) => r.suiteId === focusedId)
    ? focusedId
    : runs.some((r) => r.suiteId === selectedRunId)
      ? selectedRunId
      : (runs[0]?.suiteId ?? focusedId);

  // Two runs launched against the same target ("Smoke QA Suite" twice) render
  // identical labels once expanded. Only append the disambiguating start time
  // to labels that actually collide among the runs currently shown, so the
  // common single-run-per-target case stays a clean, short label.
  const labelCounts = React.useMemo(() => {
    const counts = new Map<string, number>();
    for (const run of runs) {
      const label = run.runTarget?.label ?? 'Test run';
      counts.set(label, (counts.get(label) ?? 0) + 1);
    }
    return counts;
  }, [runs]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const index = runs.findIndex((r) => r.suiteId === effectiveFocusedId);
    let nextIndex: number;
    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (index + 1) % runs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (index - 1 + runs.length) % runs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = runs.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    const nextId = runs[nextIndex]!.suiteId;
    setFocusedId(nextId);
    tabRefs.current.get(nextId)?.focus();
  };

  return (
    <div
      role="tablist"
      aria-label="Live runs"
      className="flex items-stretch flex-nowrap gap-0.5"
      onKeyDown={handleKeyDown}
    >
      {runs.map((run) => {
        const label = run.runTarget?.label ?? 'Test run';
        const isAmbiguous = (labelCounts.get(label) ?? 0) > 1;
        const displayLabel = isAmbiguous
          ? `${label} · ${formatRelativeStartTime(run.startedAt)}`
          : label;
        const isSelected = run.suiteId === selectedRunId;
        const isLive = run.executionStatus !== 'completed';
        // A run added optimistically via `addRun` has no `runTarget` yet
        // (see `useLiveWorkflowTestExecutions`) — a spinning placeholder
        // icon signals "still resolving" rather than guessing a kind.
        const KindIcon = run.runTarget ? KIND_ICON[run.runTarget.kind] : CircleNotch;

        return (
          <TooltipTrigger key={run.suiteId} delay={200}>
            <button
              ref={(el) => {
                if (el) tabRefs.current.set(run.suiteId, el);
                else tabRefs.current.delete(run.suiteId);
              }}
              type="button"
              role="tab"
              id={liveRunTabId(run.suiteId)}
              aria-selected={isSelected}
              aria-controls={panelId}
              aria-label={displayLabel}
              tabIndex={run.suiteId === effectiveFocusedId ? 0 : -1}
              onFocus={() => setFocusedId(run.suiteId)}
              onClick={() => {
                setFocusedId(run.suiteId);
                onRunSelect(run.suiteId);
              }}
              className={cn(
                // `rounded-t-md` + `filter-lockup-table` match the filter-bar/
                // sparkline/table lockup's own radius and (under the Cofactor
                // brand) hairline color — see `MATCH_LOCKUP_BORDER` in
                // `matchLockupBorder.ts`. The tabs are this card's frame just as
                // much as the card body is, so they carry the same hook.
                'flex min-w-0 items-center gap-1.5 rounded-t-md filter-lockup-table border border-b-0 px-2 py-1.5',
                'text-sm font-medium',
                // `flex-grow`/`flex-basis` are animatable, same as the label's
                // `grid-template-columns` below — both are driven by the same
                // `isSelected` flip on the same duration/easing, so the tab's
                // outer width and its label reveal move together instead of
                // one snapping ahead of the other.
                'transition-[flex-grow,flex-basis,background-color,color] duration-200 ease-out motion-reduce:transition-none',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:z-10',
                isSelected
                  ? 'flex-1 bg-card text-foreground border-border z-10'
                  : // Real chrome at rest — an opaque fill and a full-strength
                    // border, not a bare icon — so the collapsed state still
                    // reads as "a tab", not a floating glyph. No hover/focus
                    // width change: only selection expands a tab now.
                    'flex-none bg-muted text-muted-foreground border-border hover:bg-muted/70 hover:text-foreground'
              )}
            >
              <span className="relative flex h-1.5 w-1.5 shrink-0">
                {isLive && (
                  <span
                    className="absolute inline-flex h-full w-full rounded-full bg-green-500"
                    style={{ animation: 'subtle-pulse 3s ease-in-out infinite' }}
                  />
                )}
                <span
                  className={cn(
                    'relative inline-flex h-1.5 w-1.5 rounded-full',
                    isLive ? 'bg-green-500' : 'bg-muted-foreground/40'
                  )}
                />
              </span>
              <KindIcon
                size={14}
                className={cn('shrink-0', !run.runTarget && 'animate-spin')}
                aria-hidden="true"
              />
              {/* `grid-template-columns` 0fr→1fr is what actually animates
                  here: a plain `width: auto` can't be transitioned, and
                  measuring the label in JS to animate a pixel width would
                  re-run on every render. The inner span's `truncate` clips
                  the always-present text (with an ellipsis once selected, if
                  the row is too narrow) without ever removing it from the
                  DOM. Toggled by `isSelected` alone now — no hover/focus
                  variant. */}
              <span
                className={cn(
                  'grid transition-[grid-template-columns] duration-200 ease-out motion-reduce:transition-none',
                  isSelected ? 'grid-cols-[1fr]' : 'grid-cols-[0fr]'
                )}
              >
                <span className="truncate">{displayLabel}</span>
              </span>
            </button>
            <Tooltip>{displayLabel}</Tooltip>
          </TooltipTrigger>
        );
      })}
    </div>
  );
}
